import { APP_CONFIG } from '@config/index';
import {
  faKey,
  faPen,
  faPlus,
  faTrash,
  faCheck,
  faCircleInfo,
  faFilePdf,
  faClose,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { usePageNavigation } from '@shared/components/navigation';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
} from '@shared/components/ui';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { Switch } from '@shared/components/ui/shadcn/switch';
import { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from '@shared/hooks/use-toast';
import { useCountries, useCreateClient, useRates, useShippingRulePresets, useUsers } from '../hooks';
import { BillingScenario, ClientFormData } from '../types';
import { ClientFormSkeleton } from './skeletons';
import { VATCalculator } from './VATCalculator';
import { SignedContractsModal, type SignedContractsModalRef } from './SignedContractsModal';

const CREATE_BANK_ACCOUNT_CURRENCY_CODES = [
  'EUR',
  'MXN',
  'USD',
  'GBP',
  'COP',
  'CLP',
  'PEN',
  'ARS',
  'BRL',
  'CHF',
  'PLN',
  'RON',
  'SEK',
] as const;

export const ClientForm = () => {
  const { t } = useTranslation('createEditClientsModule');
  const navigate = useNavigate();
  const teamCurrency = useCurrency();

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    clearErrors,
    watch,
    control,
  } = useForm<ClientFormData>({
    defaultValues: {
      name: '',
      vat: '',
      address: '',
      zip: '',
      city: '',
      region: '',
      country: 'ES',
      type: 'tenant_external',
      userName: '',
      userEmail: '',
      userPassword: '',
      userPhone: '',
      commercialId: null,
      companyType: 'sl',
      customerOrigin: 'organic',
      billingFrequency: 'monthly',
      orderType: 'card',
      storeUrl: '',
      monthlyOrderEstimate: null,
      orderAttachmentsEnabled: false,
      billingScenario: 'compensates',
      invoiceWalletCompensation: true,
      walletVisible: true,
      invoiceExpiration: 0,
      codAdvanceDaysAfterDelivery: 0,
      holdedId: '',
      holdedClientAccount: '',
      taxes: null,
      rateId: null,
      envelopeId: '',
      requiresContract: undefined,
      shippingRulesPreset: null,
      contacts: [
        {
          name: '',
          email: '',
          phone: '',
          role: '',
        },
      ],
      bankAccountHolderName: '',
      bankAccountIban: '',
      bankAccountBic: '',
      bankAccountBankName: '',
      bankAccountCountryCode: 'ES',
      bankAccountCurrency:
        teamCurrency.trim().toUpperCase() || (APP_CONFIG.defaultCurrency || '').trim().toUpperCase() || 'EUR',
      bankAccountInternalNote: '',
      bankAccountProofDocumentFile: null,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'contacts',
  });

  // Callback para manejar errores de validación de la API
  const handleValidationErrors = (validationErrors: Record<string, string[]>) => {
    // Mapear errores de la API a los campos del formulario
    const errorMap: Record<string, keyof ClientFormData> = {
      name: 'name',
      vat: 'vat',
      address: 'address',
      zip: 'zip',
      city: 'city',
      region: 'region',
      country: 'country',
      'user.name': 'userName',
      'user.email': 'userEmail',
      'user.password': 'userPassword',
      'user.phone': 'userPhone',
      'beeping_teams.commercial_id': 'commercialId',
      'beeping_teams.company_type': 'companyType',
      'beeping_teams.customer_origin': 'customerOrigin',
      'beeping_teams.billing_frequency': 'billingFrequency',
      'beeping_teams.order_type': 'orderType',
      'beeping_teams.store_url': 'storeUrl',
      'beeping_teams.monthly_order_estimate': 'monthlyOrderEstimate',
      'beeping_teams.order_attachments_enabled': 'orderAttachmentsEnabled',
      'beeping_teams.invoice_wallet_compensation': 'invoiceWalletCompensation',
      'beeping_teams.wallet_visible': 'walletVisible',
      'beeping_teams.invoice_expiration': 'invoiceExpiration',
      'beeping_teams.cod_advance_days_after_delivery': 'codAdvanceDaysAfterDelivery',
      'beeping_teams.holded_id': 'holdedId',
      'beeping_teams.holded_client_account': 'holdedClientAccount',
      'beeping_teams.taxes': 'taxes',
      rate_id: 'rateId',
      envelope_id: 'envelopeId',
      'bank_account.holder_name': 'bankAccountHolderName',
      'bank_account.iban': 'bankAccountIban',
      'bank_account.bic': 'bankAccountBic',
      'bank_account.bank_name': 'bankAccountBankName',
      'bank_account.country_code': 'bankAccountCountryCode',
      'bank_account.currency': 'bankAccountCurrency',
      'bank_account.internal_note': 'bankAccountInternalNote',
      'bank_account.proof_document_file': 'bankAccountProofDocumentFile',
    };

    Object.entries(validationErrors).forEach(([field, messages]) => {
      // Manejar errores de contactos (formato: beeping_team_contacts.0.name)
      if (field.startsWith('beeping_team_contacts.')) {
        const match = field.match(/beeping_team_contacts\.(\d+)\.(\w+)/);
        if (match) {
          const index = parseInt(match[1]);
          const contactField = match[2] as 'name' | 'email' | 'phone' | 'role';
          setError(`contacts.${index}.${contactField}` as any, {
            type: 'manual',
            message: messages[0],
          });
        }
      } else {
        const formField = errorMap[field];
        if (formField) {
          setError(formField, {
            type: 'manual',
            message: messages[0], // Mostrar el primer error
          });
        }
      }
    });
  };

  const { createClient, isCreating } = useCreateClient(handleValidationErrors);
  const { countries, loading: countriesLoading } = useCountries();
  const { users, loading: usersLoading } = useUsers();
  const { rates, loading: ratesLoading } = useRates();
  const { presets, loading: presetsLoading } = useShippingRulePresets();

  const signedContractsModalRef = useRef<SignedContractsModalRef>(null);

  const [selectedCountry, setSelectedCountry] = useState('ES');
  const [selectedBankCountry, setSelectedBankCountry] = useState('ES');

  // Watch para escenario de facturación y frecuencia
  const billingScenario = watch('billingScenario');
  const billingFrequency = watch('billingFrequency');

  // Efecto para manejar cambios de escenario de facturación
  useEffect(() => {
    if (billingScenario === 'compensates') {
      // Escenario 1: Compensa
      setValue('invoiceWalletCompensation', true);
      setValue('walletVisible', true);
      setValue('invoiceExpiration', 0);
    } else if (billingScenario === 'no_compensates') {
      // Escenario 2: No compensa
      setValue('invoiceWalletCompensation', false);
      setValue('codAdvanceDaysAfterDelivery', 0);
      // invoiceExpiration queda flexible (el usuario puede elegir)
    }
  }, [billingScenario, setValue]); // Solo depende de billingScenario

  // Efecto para manejar cambios de frecuencia dentro del escenario "compensa"
  useEffect(() => {
    if (billingScenario === 'compensates') {
      // Mensual + Compensa = no permite adelantar reembolsos
      if (billingFrequency === 'monthly') {
        setValue('codAdvanceDaysAfterDelivery', 0);
      }
    }
    // En escenario "no compensa", siempre es 0
    if (billingScenario === 'no_compensates') {
      setValue('codAdvanceDaysAfterDelivery', 0);
    }
  }, [billingFrequency, billingScenario, setValue]);

  // Función para manejar cambio de escenario
  const handleScenarioChange = (scenario: BillingScenario) => {
    setValue('billingScenario', scenario);
  };

  // Función para generar contraseña aleatoria
  const generatePassword = () => {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setValue('userPassword', password);
  };

  // Convertir países a opciones para el combobox
  const countryOptions = countries.map((country) => ({
    label: country.name,
    value: country.iso2,
  }));

  const bankAccountCurrencyOptions = useMemo(() => {
    const normalized = teamCurrency.trim().toUpperCase();
    const base = [...CREATE_BANK_ACCOUNT_CURRENCY_CODES] as string[];
    if (normalized && !base.includes(normalized)) {
      base.unshift(normalized);
    }
    return base;
  }, [teamCurrency]);

  // Convertir usuarios a opciones para el combobox
  const userOptions = users.map((user) => ({
    label: `${user.name} (${user.email})`,
    value: user.id.toString(),
  }));

  const handleCountryChange = (value: string) => {
    setSelectedCountry(value);
    setValue('country', value);
  };

  const handleBankCountryChange = (value: string) => {
    setSelectedBankCountry(value);
    setValue('bankAccountCountryCode', value);
  };

  const handleCommercialChange = (value: string) => {
    setValue('commercialId', value ? parseInt(value) : null);
  };

  const handleRateChange = (value: string) => {
    setValue('rateId', value ? parseInt(value) : null);
  };

  const handleContractSelected = (envelopeId: string) => {
    setValue('envelopeId', envelopeId);
    if (envelopeId && watch('requiresContract') === undefined) {
      setValue('requiresContract', true);
    }
  };

  // Memoizar la función onChange para el VATCalculator para evitar bucles infinitos
  const handleTaxesChange = useCallback(
    (value: string) => {
      setValue('taxes', value ? [value] : null);
      clearErrors('taxes');
    },
    [setValue, clearErrors],
  );

  // Convertir tarifas a opciones para el combobox
  const rateOptions = rates.map((rate: any) => ({
    label: rate.title || rate.name || `Tarifa ${rate.id}`,
    value: rate.id.toString(),
  }));

  // Breadcrumbs
  const breadcrumbs = useMemo(
    () => [{ label: t('breadcrumbs.clients'), path: '/clients' }, { label: t('breadcrumbs.createClient') }],
    [t],
  );

  usePageNavigation({ breadcrumbs });

  // Mostrar skeleton solo mientras se cargan los países (dato crítico para el formulario)
  if (countriesLoading) {
    return <ClientFormSkeleton />;
  }

  const onSubmit = (data: ClientFormData) => {
    // Validar que se haya calculado el IVA
    if (data.taxes === null || data.taxes === undefined || data.taxes.length === 0) {
      setError('taxes', {
        type: 'manual',
        message: t('validation.required'),
      });
      toast({
        variant: 'destructive',
        title: 'Formulario incompleto',
        description: 'Completa el cálculo de IVA en la sección "Integración Holded" antes de continuar.',
      });
      return;
    }

    // Validar que al menos un contacto tenga nombre
    const validContacts = data.contacts.filter((contact) => contact.name && contact.name.trim() !== '');

    if (validContacts.length === 0) {
      setError('contacts.0.name', {
        type: 'manual',
        message: t('validation.required'),
      });
      toast({
        variant: 'destructive',
        title: 'Formulario incompleto',
        description: 'Añade al menos un contacto con nombre.',
      });
      return;
    }

    // Filtrar contactos vacíos y construir el payload
    const contactsPayload = validContacts.map((contact) => ({
      name: contact.name,
      email: contact.email || null,
      phone: contact.phone || null,
      role: contact.role || null,
    }));

    const currencyCode =
      data.bankAccountCurrency.trim().toUpperCase() ||
      teamCurrency.trim().toUpperCase() ||
      (APP_CONFIG.defaultCurrency || '').trim().toUpperCase() ||
      'EUR';

    const payload: any = {
      name: data.name,
      vat: data.vat,
      address: data.address,
      zip: data.zip,
      city: data.city,
      region: data.region,
      country: data.country,
      currency: currencyCode,
      user: {
        name: data.userName,
        email: data.userEmail,
        password: data.userPassword,
        phone: data.userPhone,
      },
      beeping_teams: {
        commercial_id: data.commercialId,
        company_type: data.companyType,
        customer_origin: data.customerOrigin,
        billing_frequency: data.billingFrequency,
        order_type: data.orderType,
        store_url: data.storeUrl || null,
        monthly_order_estimate: data.monthlyOrderEstimate,
        order_attachments_enabled: data.orderAttachmentsEnabled,
        invoice_wallet_compensation: data.invoiceWalletCompensation,
        wallet_visible: data.walletVisible,
        invoice_expiration: data.invoiceExpiration,
        cod_advance_days_after_delivery: data.codAdvanceDaysAfterDelivery!,
        holded_id: data.holdedId,
        holded_client_account: data.holdedClientAccount,
        taxes: data.taxes!,
      },
      beeping_team_contacts: contactsPayload,
      bank_account: {
        holder_name: data.bankAccountHolderName,
        iban: data.bankAccountIban,
        bic: data.bankAccountBic,
        bank_name: data.bankAccountBankName,
        country_code: data.bankAccountCountryCode,
        currency: currencyCode,
        internal_note: data.bankAccountInternalNote,
        proof_document_file: data.bankAccountProofDocumentFile || undefined,
      },
      rate_id: data.rateId!,
    };

    // Solo incluir envelope_id si tiene valor
    if (data.envelopeId) {
      payload.envelope_id = data.envelopeId;
    }

    // Solo incluir requires_contract si el usuario lo especificó explícitamente
    // Si no se incluye, el backend lo infiere automáticamente basado en contract_template_id
    if (data.requiresContract !== undefined) {
      payload.requires_contract = data.requiresContract;
    }

    if (data.shippingRulesPreset) {
      payload.shipping_rule_preset_id = data.shippingRulesPreset;
    }

    createClient(payload);
  };

  const onInvalidSubmit = (formErrors: Record<string, any>) => {
    const messages: string[] = [];
    const flatten = (obj: any, prefix = '') => {
      Object.entries(obj).forEach(([key, val]: [string, any]) => {
        const path = prefix ? `${prefix}.${key}` : key;
        if (val?.message) {
          messages.push(`${path}: ${val.message}`);
        } else if (val && typeof val === 'object' && !val.type) {
          flatten(val, path);
        }
      });
    };
    flatten(formErrors);
    toast({
      variant: 'destructive',
      title: 'Formulario incompleto',
      description: messages.length > 0 ? messages.join(' · ') : 'Revisa los campos obligatorios.',
    });
    console.error('[ClientForm] validation errors', formErrors);
  };

  return (
    <>
      {/* Botones de acción */}
      <div className="flex items-center flex-wrap gap-3 absolute right-4 top-2">
        <Button
          type="button"
          variant="outline"
          size="default"
          className="shrink-0"
          onClick={() => navigate('/clients')}
          disabled={isCreating}
        >
          {t('buttons.cancel')}
        </Button>
        <Button
          type="submit"
          variant="primary"
          size="default"
          className="shrink-0"
          disabled={isCreating}
          onClick={handleSubmit(onSubmit, onInvalidSubmit)}
        >
          {isCreating ? t('buttons.creating') : t('buttons.create')}
        </Button>
      </div>

      <div className="px-6 pt-4">
        <form onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}>
          {/* Información de la Empresa */}
          <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4 border-b border-card-border">
            <div className="col-span-1 md:col-span-2">
              <h2 className="font-semibold text-lg mb-1">{t('sections.company')}</h2>
              <p className="text-sm text-muted-foreground">{t('sections.companyDescription')}</p>
            </div>
            <div className="col-span-1 md:col-span-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Nombre empresa - CIF */}
                <div className="flex flex-col gap-1">
                  <Label htmlFor="name" className="pb-2">
                    {t('fields.name')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="name"
                    {...register('name', {
                      required: t('validation.required'),
                    })}
                    placeholder={t('placeholders.name')}
                    className={errors.name ? 'border-destructive' : ''}
                  />
                  {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
                </div>

                <div className="flex flex-col gap-1">
                  <Label htmlFor="vat" className="pb-2">
                    {t('fields.vat')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="vat"
                    {...register('vat', {
                      required: t('validation.required'),
                    })}
                    placeholder={t('placeholders.vat')}
                    className={errors.vat ? 'border-destructive' : ''}
                  />
                  {errors.vat && <p className="text-sm text-destructive">{errors.vat.message}</p>}
                </div>

                {/* Dirección */}
                <div className="flex flex-col gap-1 md:col-span-2">
                  <Label htmlFor="address" className="pb-2">
                    {t('fields.address')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="address"
                    {...register('address', {
                      required: t('validation.required'),
                    })}
                    placeholder={t('placeholders.address')}
                    className={errors.address ? 'border-destructive' : ''}
                  />
                  {errors.address && <p className="text-sm text-destructive">{errors.address.message}</p>}
                </div>

                {/* Código postal - Ciudad */}
                <div className="flex flex-col gap-1">
                  <Label htmlFor="zip" className="pb-2">
                    {t('fields.zip')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="zip"
                    {...register('zip', {
                      required: t('validation.required'),
                    })}
                    placeholder={t('placeholders.zip')}
                    className={errors.zip ? 'border-destructive' : ''}
                  />
                  {errors.zip && <p className="text-sm text-destructive">{errors.zip.message}</p>}
                </div>

                <div className="flex flex-col gap-1">
                  <Label htmlFor="city" className="pb-2">
                    {t('fields.city')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="city"
                    {...register('city', {
                      required: t('validation.required'),
                    })}
                    placeholder={t('placeholders.city')}
                    className={errors.city ? 'border-destructive' : ''}
                  />
                  {errors.city && <p className="text-sm text-destructive">{errors.city.message}</p>}
                </div>

                {/* Provincia - País */}
                <div className="flex flex-col gap-1">
                  <Label htmlFor="region" className="pb-2">
                    {t('fields.region')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="region"
                    {...register('region', {
                      required: t('validation.required'),
                    })}
                    placeholder={t('placeholders.region')}
                    className={errors.region ? 'border-destructive' : ''}
                  />
                  {errors.region && <p className="text-sm text-destructive">{errors.region.message}</p>}
                </div>

                <div className="flex flex-col gap-1">
                  <Label htmlFor="country" className="pb-2">
                    {t('fields.country')} <span className="text-destructive">*</span>
                  </Label>
                  <Combobox
                    options={countryOptions}
                    placeholder={t('placeholders.country')}
                    value={selectedCountry}
                    setValue={handleCountryChange}
                    className="w-full"
                    disabled={countriesLoading}
                  />
                  {errors.country && <p className="text-sm text-destructive">{errors.country.message}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Información del Usuario */}
          <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4 border-b border-card-border">
            <div className="col-span-1 md:col-span-2">
              <h2 className="font-semibold text-lg mb-1">{t('sections.user')}</h2>
              <p className="text-sm text-muted-foreground">{t('sections.userDescription')}</p>
            </div>
            <div className="col-span-1 md:col-span-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1">
                  <Label htmlFor="userName" className="pb-2">
                    {t('fields.userName')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="userName"
                    {...register('userName', {
                      required: t('validation.required'),
                    })}
                    placeholder={t('placeholders.userName')}
                    className={errors.userName ? 'border-destructive' : ''}
                  />
                  {errors.userName && <p className="text-sm text-destructive">{errors.userName.message}</p>}
                </div>

                <div className="flex flex-col gap-1">
                  <Label htmlFor="userEmail" className="pb-2">
                    {t('fields.userEmail')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="userEmail"
                    type="email"
                    {...register('userEmail', {
                      required: t('validation.required'),
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t('validation.invalidEmail'),
                      },
                    })}
                    placeholder={t('placeholders.userEmail')}
                    className={errors.userEmail ? 'border-destructive' : ''}
                  />
                  {errors.userEmail && <p className="text-sm text-destructive">{errors.userEmail.message}</p>}
                </div>

                <div className="flex flex-col gap-1">
                  <Label htmlFor="userPassword" className="pb-2">
                    {t('fields.userPassword')} <span className="text-destructive">*</span>
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="userPassword"
                      type="text"
                      {...register('userPassword', {
                        required: t('validation.required'),
                        minLength: { value: 6, message: t('validation.minLength', { count: 6 }) },
                      })}
                      placeholder={t('placeholders.userPassword')}
                      className={errors.userPassword ? 'border-destructive' : ''}
                      autoComplete="new-password"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={generatePassword}
                      title={t('buttons.generatePassword')}
                    >
                      <FontAwesomeIcon icon={faKey} />
                    </Button>
                  </div>
                  {errors.userPassword && <p className="text-sm text-destructive">{errors.userPassword.message}</p>}
                </div>

                <div className="flex flex-col gap-1">
                  <Label htmlFor="userPhone" className="pb-2">
                    {t('fields.userPhone')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="userPhone"
                    type="tel"
                    {...register('userPhone', {
                      required: t('validation.required'),
                    })}
                    placeholder={t('placeholders.userPhone')}
                    className={errors.userPhone ? 'border-destructive' : ''}
                  />
                  {errors.userPhone && <p className="text-sm text-destructive">{errors.userPhone.message}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* Información Comercial */}
          <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4 border-b border-card-border">
            <div className="col-span-1 md:col-span-2">
              <h2 className="font-semibold text-lg mb-1">{t('sections.commercial')}</h2>
              <p className="text-sm text-muted-foreground">{t('sections.commercialDescription')}</p>
            </div>
            <div className="col-span-1 md:col-span-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* ID Comercial */}
                <div className="flex flex-col gap-1 md:col-span-2">
                  <Label htmlFor="commercialId" className="pb-2">
                    {t('fields.commercialId')}
                  </Label>
                  <Combobox
                    options={userOptions}
                    placeholder={t('placeholders.commercialId')}
                    value={watch('commercialId')?.toString() || ''}
                    setValue={handleCommercialChange}
                    className="w-full"
                    disabled={usersLoading}
                  />
                  {errors.commercialId && <p className="text-sm text-destructive">{errors.commercialId.message}</p>}
                </div>

                {/* Tipo de Empresa */}
                <div className="flex flex-col gap-1">
                  <Label htmlFor="companyType" className="pb-2">
                    {t('fields.companyType')} <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={watch('companyType')}
                    onValueChange={(value) => setValue('companyType', value as 'sl' | 'freelancer')}
                  >
                    <SelectTrigger id="companyType" className={errors.companyType ? 'border-destructive' : ''}>
                      <SelectValue placeholder={t('placeholders.companyType')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sl">{t('companyTypes.sl')}</SelectItem>
                      <SelectItem value="freelancer">{t('companyTypes.freelancer')}</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.companyType && <p className="text-sm text-destructive">{errors.companyType.message}</p>}
                </div>

                {/* Origen del Cliente */}
                <div className="flex flex-col gap-1">
                  <Label htmlFor="customerOrigin" className="pb-2">
                    {t('fields.customerOrigin')} <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={watch('customerOrigin')}
                    onValueChange={(value) => setValue('customerOrigin', value as any)}
                  >
                    <SelectTrigger id="customerOrigin" className={errors.customerOrigin ? 'border-destructive' : ''}>
                      <SelectValue placeholder={t('placeholders.customerOrigin')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="organic">{t('customerOrigins.organic')}</SelectItem>
                      <SelectItem value="student">{t('customerOrigins.student')}</SelectItem>
                      <SelectItem value="affiliate">{t('customerOrigins.affiliate')}</SelectItem>
                      <SelectItem value="meta_ads">{t('customerOrigins.meta_ads')}</SelectItem>
                      <SelectItem value="linkedin_ads">{t('customerOrigins.linkedin_ads')}</SelectItem>
                      <SelectItem value="google_ads">{t('customerOrigins.google_ads')}</SelectItem>
                      <SelectItem value="linkedin">{t('customerOrigins.linkedin')}</SelectItem>
                      <SelectItem value="instagram">{t('customerOrigins.instagram')}</SelectItem>
                      <SelectItem value="blog">{t('customerOrigins.blog')}</SelectItem>
                      <SelectItem value="instagram_prospecting">
                        {t('customerOrigins.instagram_prospecting')}
                      </SelectItem>
                      <SelectItem value="linkedin_prospecting">{t('customerOrigins.linkedin_prospecting')}</SelectItem>
                      <SelectItem value="cold_email">{t('customerOrigins.cold_email')}</SelectItem>
                      <SelectItem value="cold_calling">{t('customerOrigins.cold_calling')}</SelectItem>
                      <SelectItem value="referral">{t('customerOrigins.referral')}</SelectItem>
                      <SelectItem value="unknown">{t('customerOrigins.unknown')}</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.customerOrigin && <p className="text-sm text-destructive">{errors.customerOrigin.message}</p>}
                </div>

                {/* Tipo de Pedido */}
                <div className="flex flex-col gap-1">
                  <Label htmlFor="orderType" className="pb-2">
                    {t('fields.orderType')} <span className="text-destructive">*</span>
                  </Label>
                  <Select value={watch('orderType')} onValueChange={(value) => setValue('orderType', value as any)}>
                    <SelectTrigger id="orderType" className={errors.orderType ? 'border-destructive' : ''}>
                      <SelectValue placeholder={t('placeholders.orderType')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cod">{t('orderTypes.cod')}</SelectItem>
                      <SelectItem value="card">{t('orderTypes.card')}</SelectItem>
                      <SelectItem value="hybrid">{t('orderTypes.hybrid')}</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.orderType && <p className="text-sm text-destructive">{errors.orderType.message}</p>}
                </div>

                {/* URL de Tienda */}
                <div className="flex flex-col gap-1">
                  <Label htmlFor="storeUrl" className="pb-2">
                    {t('fields.storeUrl')}
                  </Label>
                  <Input
                    id="storeUrl"
                    type="url"
                    {...register('storeUrl', {
                      pattern: {
                        value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
                        message: t('validation.invalidUrl'),
                      },
                    })}
                    placeholder={t('placeholders.storeUrl')}
                    className={errors.storeUrl ? 'border-destructive' : ''}
                  />
                  {errors.storeUrl && <p className="text-sm text-destructive">{errors.storeUrl.message}</p>}
                </div>

                {/* Estimación Mensual de Pedidos */}
                <div className="flex flex-col gap-1">
                  <Label htmlFor="monthlyOrderEstimate" className="pb-2">
                    {t('fields.monthlyOrderEstimate')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="monthlyOrderEstimate"
                    type="number"
                    min="0"
                    {...register('monthlyOrderEstimate', {
                      setValueAs: (v) => {
                        if (v === '' || v === null || v === undefined) {
                          return null;
                        }
                        const numValue = Number(v);
                        return isNaN(numValue) ? null : numValue;
                      },
                      validate: (value) => {
                        if (value === null || value === undefined) {
                          return t('validation.required');
                        }
                        if (typeof value === 'number' && (isNaN(value) || value < 0)) {
                          return t('validation.required');
                        }
                        return true;
                      },
                    })}
                    placeholder={t('placeholders.monthlyOrderEstimate')}
                    className={errors.monthlyOrderEstimate ? 'border-destructive' : ''}
                  />
                  {errors.monthlyOrderEstimate && (
                    <p className="text-sm text-destructive">{errors.monthlyOrderEstimate.message}</p>
                  )}
                </div>

                {/* Switch de adjuntos en pedidos */}
                <div className="flex items-center gap-3 md:col-span-2 pt-4">
                  <Controller
                    name="orderAttachmentsEnabled"
                    control={control}
                    render={({ field }) => (
                      <Switch id="orderAttachmentsEnabled" checked={field.value} onCheckedChange={field.onChange} />
                    )}
                  />
                  <Label htmlFor="orderAttachmentsEnabled" className="cursor-pointer">
                    {t('fields.orderAttachmentsEnabled')}
                  </Label>
                </div>
              </div>
            </div>
          </div>

          {/* Escenario de Facturación */}
          <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4 border-b border-card-border">
            <div className="col-span-1 md:col-span-2">
              <h2 className="font-semibold text-lg mb-1">{t('sections.billing')}</h2>
              <p className="text-sm text-muted-foreground">{t('sections.billingDescription')}</p>
            </div>
            <div className="col-span-1 md:col-span-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Selector de Escenario de Facturación */}
                <div className="flex flex-col gap-2 md:col-span-2">
                  <Label className="pb-1">
                    {t('billingScenarios.label')} <span className="text-destructive">*</span>
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {/* Escenario 1: Compensa */}
                    <button
                      type="button"
                      onClick={() => handleScenarioChange('compensates')}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        billingScenario === 'compensates'
                          ? 'border-primary bg-primary/5 ring-1 ring-primary'
                          : 'border-border hover:border-primary/50 hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            billingScenario === 'compensates' ? 'border-primary' : 'border-muted-foreground'
                          }`}
                        >
                          {billingScenario === 'compensates' && <div className="w-2 h-2 rounded-full bg-primary" />}
                        </div>
                        <span className="font-medium">{t('billingScenarios.compensates.title')}</span>
                      </div>
                      <p className="text-sm text-muted-foreground ml-6">
                        {t('billingScenarios.compensates.description')}
                      </p>
                    </button>

                    {/* Escenario 2: No Compensa */}
                    <button
                      type="button"
                      onClick={() => handleScenarioChange('no_compensates')}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        billingScenario === 'no_compensates'
                          ? 'border-primary bg-primary/5 ring-1 ring-primary'
                          : 'border-border hover:border-primary/50 hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            billingScenario === 'no_compensates' ? 'border-primary' : 'border-muted-foreground'
                          }`}
                        >
                          {billingScenario === 'no_compensates' && <div className="w-2 h-2 rounded-full bg-primary" />}
                        </div>
                        <span className="font-medium">{t('billingScenarios.no_compensates.title')}</span>
                      </div>
                      <p className="text-sm text-muted-foreground ml-6">
                        {t('billingScenarios.no_compensates.description')}
                      </p>
                    </button>
                  </div>
                </div>

                {/* Frecuencia de Facturación */}
                <div className="flex flex-col gap-1">
                  <Label htmlFor="billingFrequency" className="pb-2">
                    {t('fields.billingFrequency')} <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={billingFrequency}
                    onValueChange={(value) => setValue('billingFrequency', value as any)}
                  >
                    <SelectTrigger
                      id="billingFrequency"
                      className={errors.billingFrequency ? 'border-destructive' : ''}
                    >
                      <SelectValue placeholder={t('placeholders.billingFrequency')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">{t('billingFrequencies.weekly')}</SelectItem>
                      <SelectItem value="monthly">{t('billingFrequencies.monthly')}</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.billingFrequency && (
                    <p className="text-sm text-destructive">{errors.billingFrequency.message}</p>
                  )}
                </div>

                {/* Días para expiración de factura - Solo visible en escenario "No compensa" */}
                {billingScenario === 'no_compensates' && (
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="invoiceExpiration" className="pb-2">
                      {t('fields.invoiceExpiration')}
                    </Label>
                    <Input
                      id="invoiceExpiration"
                      type="number"
                      min="0"
                      {...register('invoiceExpiration', {
                        setValueAs: (v) => {
                          if (v === '' || v === null || v === undefined) {
                            return null;
                          }
                          const numValue = Number(v);
                          return isNaN(numValue) ? null : numValue;
                        },
                      })}
                      placeholder={t('placeholders.invoiceExpiration')}
                      className={errors.invoiceExpiration ? 'border-destructive' : ''}
                    />
                    {errors.invoiceExpiration && (
                      <p className="text-sm text-destructive">{errors.invoiceExpiration.message}</p>
                    )}
                  </div>
                )}

                {/* Días adelanto COD tras entrega - Solo visible en escenario "Compensa" + Semanal */}
                {billingScenario === 'compensates' && billingFrequency === 'weekly' && (
                  <div className="flex flex-col gap-1">
                    <Label htmlFor="codAdvanceDaysAfterDelivery" className="pb-2">
                      {t('fields.codAdvanceDaysAfterDelivery')} <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="codAdvanceDaysAfterDelivery"
                      type="number"
                      min="0"
                      {...register('codAdvanceDaysAfterDelivery', {
                        setValueAs: (v) => {
                          if (v === '' || v === null || v === undefined) {
                            return null;
                          }
                          const numValue = Number(v);
                          return isNaN(numValue) ? null : numValue;
                        },
                        validate: (value) => {
                          if (value === null || value === undefined) {
                            return t('validation.required');
                          }
                          if (typeof value === 'number' && (isNaN(value) || value < 0)) {
                            return t('validation.required');
                          }
                          return true;
                        },
                      })}
                      placeholder={t('placeholders.codAdvanceDaysAfterDelivery')}
                      className={errors.codAdvanceDaysAfterDelivery ? 'border-destructive' : ''}
                    />
                    {errors.codAdvanceDaysAfterDelivery && (
                      <p className="text-sm text-destructive">{errors.codAdvanceDaysAfterDelivery.message}</p>
                    )}
                  </div>
                )}

                {/* Switches de facturación */}
                <div className="flex items-center gap-3 md:col-span-2 pt-2">
                  <Controller
                    name="invoiceWalletCompensation"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        id="invoiceWalletCompensation"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={true}
                      />
                    )}
                  />
                  <Label htmlFor="invoiceWalletCompensation" className="cursor-pointer text-muted-foreground">
                    {t('fields.invoiceWalletCompensation')}
                  </Label>
                </div>

                <div className="flex items-center gap-3 md:col-span-2">
                  <Controller
                    name="walletVisible"
                    control={control}
                    render={({ field }) => (
                      <Switch id="walletVisible" checked={field.value} onCheckedChange={field.onChange} />
                    )}
                  />
                  <Label htmlFor="walletVisible" className="cursor-pointer">
                    {t('fields.walletVisible')}
                  </Label>
                </div>
              </div>
            </div>
          </div>

          {/* Integración Holded */}
          <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4 border-b border-card-border">
            <div className="col-span-1 md:col-span-2">
              <h2 className="font-semibold text-lg mb-1">{t('sections.holdedIntegration')}</h2>
              <p className="text-sm text-muted-foreground">{t('sections.holdedIntegrationDescription')}</p>
            </div>
            <div className="col-span-1 md:col-span-4">
              <div className="space-y-6">
                {/* Determina el tipo de IVA */}
                <div className="space-y-4">
                  <div>
                    <h3 className="font-semibold text-base mb-1">{t('vatCalculator.title')}</h3>
                    <p className="text-sm text-muted-foreground">{t('vatCalculator.description')}</p>
                  </div>
                  <VATCalculator
                    value={watch('taxes')?.[0] || null}
                    onChange={handleTaxesChange}
                    country={watch('country')}
                    region={watch('region')}
                    vat={watch('vat')}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                {/* ID de Holded */}
                <div className="flex flex-col gap-1">
                  <Label htmlFor="holdedId" className="pb-2">
                    {t('fields.holdedId')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="holdedId"
                    {...register('holdedId', {
                      required: t('validation.required'),
                    })}
                    placeholder={t('placeholders.holdedId')}
                    className={errors.holdedId ? 'border-destructive' : ''}
                  />
                  {errors.holdedId && <p className="text-sm text-destructive">{errors.holdedId.message}</p>}
                </div>

                {/* Cuenta Cliente Holded */}
                <div className="flex flex-col gap-1">
                  <Label htmlFor="holdedClientAccount" className="pb-2">
                    {t('fields.holdedClientAccount')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="holdedClientAccount"
                    {...register('holdedClientAccount', {
                      required: t('validation.required'),
                    })}
                    placeholder={t('placeholders.holdedClientAccount')}
                    className={errors.holdedClientAccount ? 'border-destructive' : ''}
                  />
                  {errors.holdedClientAccount && (
                    <p className="text-sm text-destructive">{errors.holdedClientAccount.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Cuenta Bancaria */}
          <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4 border-b border-card-border">
            <div className="col-span-1 md:col-span-2">
              <h2 className="font-semibold text-lg mb-1">{t('sections.bankAccount')}</h2>
              <p className="text-sm text-muted-foreground">{t('sections.bankAccountDescription')}</p>
            </div>
            <div className="col-span-1 md:col-span-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Titular de la Cuenta */}
                <div className="flex flex-col gap-1">
                  <Label htmlFor="bankAccountHolderName" className="pb-2">
                    {t('fields.bankAccountHolderName')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="bankAccountHolderName"
                    {...register('bankAccountHolderName', {
                      required: t('validation.required'),
                    })}
                    placeholder={t('placeholders.bankAccountHolderName')}
                    className={errors.bankAccountHolderName ? 'border-destructive' : ''}
                  />
                  {errors.bankAccountHolderName && (
                    <p className="text-sm text-destructive">{errors.bankAccountHolderName.message}</p>
                  )}
                </div>

                {/* IBAN */}
                <div className="flex flex-col gap-1">
                  <Label htmlFor="bankAccountIban" className="pb-2">
                    {t('fields.bankAccountIban')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="bankAccountIban"
                    {...register('bankAccountIban', {
                      required: t('validation.required'),
                    })}
                    placeholder={t('placeholders.bankAccountIban')}
                    className={errors.bankAccountIban ? 'border-destructive' : ''}
                  />
                  {errors.bankAccountIban && (
                    <p className="text-sm text-destructive">{errors.bankAccountIban.message}</p>
                  )}
                </div>

                {/* BIC/SWIFT */}
                <div className="flex flex-col gap-1">
                  <Label htmlFor="bankAccountBic" className="pb-2">
                    {t('fields.bankAccountBic')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="bankAccountBic"
                    {...register('bankAccountBic', {
                      required: t('validation.required'),
                    })}
                    placeholder={t('placeholders.bankAccountBic')}
                    className={errors.bankAccountBic ? 'border-destructive' : ''}
                  />
                  {errors.bankAccountBic && <p className="text-sm text-destructive">{errors.bankAccountBic.message}</p>}
                </div>

                {/* Nombre del Banco */}
                <div className="flex flex-col gap-1">
                  <Label htmlFor="bankAccountBankName" className="pb-2">
                    {t('fields.bankAccountBankName')} <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="bankAccountBankName"
                    {...register('bankAccountBankName', {
                      required: t('validation.required'),
                    })}
                    placeholder={t('placeholders.bankAccountBankName')}
                    className={errors.bankAccountBankName ? 'border-destructive' : ''}
                  />
                  {errors.bankAccountBankName && (
                    <p className="text-sm text-destructive">{errors.bankAccountBankName.message}</p>
                  )}
                </div>

                {/* Código de País */}
                <div className="flex flex-col gap-1">
                  <Label htmlFor="bankAccountCountryCode" className="pb-2">
                    {t('fields.bankAccountCountryCode')} <span className="text-destructive">*</span>
                  </Label>
                  <Combobox
                    options={countryOptions}
                    placeholder={t('placeholders.country')}
                    value={selectedBankCountry}
                    setValue={handleBankCountryChange}
                    className="w-full"
                    disabled={countriesLoading}
                  />
                  {errors.bankAccountCountryCode && (
                    <p className="text-sm text-destructive">{errors.bankAccountCountryCode.message}</p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <Label htmlFor="bankAccountCurrency" className="pb-2">
                    {t('fields.bankAccountCurrency')} <span className="text-destructive">*</span>
                  </Label>
                  <Controller
                    name="bankAccountCurrency"
                    control={control}
                    rules={{ required: t('validation.required') }}
                    render={({ field }) => (
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={isCreating || countriesLoading}
                      >
                        <SelectTrigger
                          id="bankAccountCurrency"
                          className="w-full"
                          data-testid="client-form-bank-account-currency"
                        >
                          <SelectValue placeholder={t('placeholders.bankAccountCurrency')} />
                        </SelectTrigger>
                        <SelectContent className="max-h-[min(50vh,16rem)] z-[120]">
                          {bankAccountCurrencyOptions.map((code) => (
                            <SelectItem key={code} value={code}>
                              {code}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.bankAccountCurrency && (
                    <p className="text-sm text-destructive">{errors.bankAccountCurrency.message}</p>
                  )}
                </div>

                {/* Nota Interna */}
                <div className="flex flex-col gap-1">
                  <Label htmlFor="bankAccountInternalNote" className="pb-2">
                    {t('fields.bankAccountInternalNote')}
                  </Label>
                  <Input
                    id="bankAccountInternalNote"
                    {...register('bankAccountInternalNote')}
                    placeholder={t('placeholders.bankAccountInternalNote')}
                    className={errors.bankAccountInternalNote ? 'border-destructive' : ''}
                  />
                  {errors.bankAccountInternalNote && (
                    <p className="text-sm text-destructive">{errors.bankAccountInternalNote.message}</p>
                  )}
                </div>
                {/* Certificado de titularidad (PDF) */}
                <div className="flex flex-col gap-1 md:col-span-2">
                  <Label htmlFor="bankAccountProofDocumentFile" className="pb-2">
                    {t('fields.bankAccountProofDocumentFile')}
                  </Label>
                  <Controller
                    name="bankAccountProofDocumentFile"
                    control={control}
                    render={({ field }) => (
                      <div className="flex flex-col gap-2 w-full items-start">
                        {field.value && (
                          <div className="flex items-center gap-2 text-xs text-complementary-text break-all pb-1">
                            <FontAwesomeIcon icon={faFilePdf} className="w-3.5 h-3.5" />
                            <span>{field.value.name}</span>
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={() => field.onChange(null)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  field.onChange(null);
                                }
                              }}
                              className="inline-flex items-center cursor-pointer"
                              data-testid="remove-bank-account-proof-document-file-button"
                              title={t('buttons.removeCertificate')}
                            >
                              <FontAwesomeIcon icon={faClose} className="w-3.5 h-3.5" />
                            </span>
                          </div>
                        )}
                        <Button
                          type="button"
                          variant="primary"
                          size="default"
                          onClick={() => {
                            const input = document.getElementById(
                              'bankAccountProofDocumentFile',
                            ) as HTMLInputElement | null;
                            input?.click();
                          }}
                          data-testid="bank-account-proof-document-file-button"
                        >
                          <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-2" />
                          {field.value ? t('buttons.changeCertificate') : t('buttons.chooseCertificate')}
                        </Button>
                        <Input
                          id="bankAccountProofDocumentFile"
                          type="file"
                          accept="application/pdf,.pdf"
                          data-testid="bank-account-proof-document-file-input"
                          onChange={(event) => {
                            // Si el usuario cancela el selector, se mantiene el archivo previamente adjuntado
                            const selectedFile = event.target.files?.[0];
                            if (selectedFile) {
                              field.onChange(selectedFile);
                            }
                            event.target.value = '';
                          }}
                          className={`hidden ${errors.bankAccountProofDocumentFile ? 'border-destructive' : ''}`}
                        />
                      </div>
                    )}
                  />
                  {errors.bankAccountProofDocumentFile && (
                    <p className="text-sm text-destructive">{errors.bankAccountProofDocumentFile.message}</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Tarifa y Contrato */}
          <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4 border-b border-card-border">
            <div className="col-span-1 md:col-span-2">
              <h2 className="font-semibold text-lg mb-1">{t('sections.rateContract')}</h2>
              <p className="text-sm text-muted-foreground">{t('sections.rateContractDescription')}</p>
            </div>
            <div className="col-span-1 md:col-span-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Tarifa */}
                <div className="flex flex-col gap-1">
                  <Label htmlFor="rateId" className="pb-2">
                    {t('fields.rateId')} <span className="text-destructive">*</span>
                  </Label>
                  <Combobox
                    options={rateOptions}
                    placeholder={t('placeholders.rateId')}
                    value={watch('rateId')?.toString() || ''}
                    setValue={handleRateChange}
                    className="w-full"
                    disabled={ratesLoading}
                  />
                  {errors.rateId && <p className="text-sm text-destructive">{errors.rateId.message}</p>}
                </div>

                {/* Requiere Contrato */}
                <div className="flex items-center gap-3">
                  <Controller
                    name="requiresContract"
                    control={control}
                    render={({ field }) => {
                      // Inferir automáticamente si no está definido
                      const checked = field.value ?? !!watch('envelopeId');
                      return (
                        <Switch
                          id="requiresContract"
                          checked={checked}
                          onCheckedChange={(checked) => {
                            field.onChange(checked);
                            // Si se desactiva, limpiar el contrato seleccionado
                            if (!checked) {
                              setValue('envelopeId', undefined);
                            }
                          }}
                        />
                      );
                    }}
                  />
                  <Label htmlFor="requiresContract" className="cursor-pointer">
                    {t('fields.requiresContract')}
                  </Label>
                </div>

                {/* Contrato firmado - Solo visible si requiere contrato */}
                {watch('requiresContract') === true && (
                  <div className="flex flex-col gap-2 md:col-span-2">
                    <Label htmlFor="envelopeId" className="pb-1">
                      {t('fields.envelopeId')}
                    </Label>
                    <div className="flex flex-col gap-2 w-full items-start">
                      {watch('envelopeId') && (
                        <span className="flex items-center gap-1 text-xs text-complementary-text break-all pb-1">
                          <FontAwesomeIcon icon={faCheck} className="w-3.5 h-3.5" />
                          <span>{t('signedContractsModal.selectedLabel')}</span>
                          <span className="text-title font-medium">{watch('envelopeId')}</span>
                        </span>
                      )}
                      {!watch('userEmail') ? (
                        <Tooltip
                          content={
                            <span className="inline-flex items-center gap-2">
                              <FontAwesomeIcon icon={faCircleInfo} className="w-3.5 h-3.5 shrink-0 text-icons" />
                              <span>{t('signedContractsModal.tooltipNoEmail')}</span>
                            </span>
                          }
                          trigger={
                            <span className="inline-block cursor-not-allowed">
                              <Button
                                type="button"
                                variant="primary"
                                size="default"
                                disabled
                                data-testid="open-signed-contracts-modal-button"
                                className="pointer-events-none"
                              >
                                <FontAwesomeIcon icon={watch('envelopeId') ? faPen : faPlus} className="w-4 h-4 mr-2" />
                                {watch('envelopeId')
                                  ? t('signedContractsModal.changeButton')
                                  : t('signedContractsModal.openButton')}
                              </Button>
                            </span>
                          }
                          data-testid="contract-button-tooltip"
                          className="text-xs"
                        />
                      ) : (
                        <Button
                          type="button"
                          variant="primary"
                          size="default"
                          onClick={() => {
                            const email = watch('userEmail');
                            if (email) {
                              signedContractsModalRef.current?.open(email);
                            }
                          }}
                          data-testid="open-signed-contracts-modal-button"
                        >
                          <FontAwesomeIcon icon={watch('envelopeId') ? faPen : faPlus} className="w-4 h-4 mr-2" />
                          {watch('envelopeId')
                            ? t('signedContractsModal.changeButton')
                            : t('signedContractsModal.openButton')}
                        </Button>
                      )}
                    </div>
                    {errors.envelopeId && <p className="text-sm text-destructive">{errors.envelopeId}</p>}
                  </div>
                )}

                {/* Reglas de envío - Presets dinámicos */}
                <div className="flex flex-col gap-2 md:col-span-2">
                  <Label className="pb-1">{t('fields.shippingRulesPreset')}</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => setValue('shippingRulesPreset', null)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        watch('shippingRulesPreset') === null
                          ? 'border-primary bg-primary/5 ring-1 ring-primary'
                          : 'border-border hover:border-primary/50 hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            watch('shippingRulesPreset') === null ? 'border-primary' : 'border-muted-foreground'
                          }`}
                        >
                          {watch('shippingRulesPreset') === null && <div className="w-2 h-2 rounded-full bg-primary" />}
                        </div>
                        <span className="font-medium">{t('shippingRulesPresets.none')}</span>
                      </div>
                      <p className="text-sm text-muted-foreground ml-6">{t('shippingRulesPresets.noneDescription')}</p>
                    </button>
                    {presetsLoading ? (
                      <div className="p-4 rounded-lg border-2 border-border animate-pulse">
                        <div className="h-4 bg-muted rounded w-24 mb-2" />
                        <div className="h-3 bg-muted rounded w-32 ml-6" />
                      </div>
                    ) : (
                      presets.map((preset) => (
                        <button
                          key={preset.id}
                          type="button"
                          onClick={() => setValue('shippingRulesPreset', preset.id)}
                          className={`p-4 rounded-lg border-2 text-left transition-all ${
                            watch('shippingRulesPreset') === preset.id
                              ? 'border-primary bg-primary/5 ring-1 ring-primary'
                              : 'border-border hover:border-primary/50 hover:bg-muted/50'
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <div
                              className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0 ${
                                watch('shippingRulesPreset') === preset.id
                                  ? 'border-primary'
                                  : 'border-muted-foreground'
                              }`}
                            >
                              {watch('shippingRulesPreset') === preset.id && (
                                <div className="w-2 h-2 rounded-full bg-primary" />
                              )}
                            </div>
                            <span className="font-medium truncate">{preset.name}</span>
                            <span className="text-xs text-muted-foreground ml-auto shrink-0">
                              {t('shippingRulesPresets.rulesCount', { count: preset.rules_count })}
                            </span>
                          </div>
                          {preset.description && (
                            <p className="text-sm text-muted-foreground ml-6 line-clamp-2">{preset.description}</p>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{t('shippingRulesPresets.hint')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contactos */}
          <div className="space-y-4 mb-6 pb-6 gap-4 grid grid-cols-1 md:grid-cols-6 pt-4">
            <div className="col-span-1 md:col-span-2">
              <h2 className="font-semibold text-lg mb-1">{t('sections.contact')}</h2>
              <p className="text-sm text-muted-foreground">{t('sections.contactDescription')}</p>
            </div>
            <div className="col-span-1 md:col-span-4">
              <div className="space-y-6">
                {fields.map((field, index) => (
                  <div key={field.id} className="space-y-4 p-4 border border-card-border rounded-lg bg-card-bg">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-medium text-foreground">
                        {t('sections.contact')} {index + 1}
                      </h3>
                      {fields.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => remove(index)}
                          className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Nombre del Contacto */}
                      <div className="flex flex-col gap-1">
                        <Label htmlFor={`contacts.${index}.name`} className="pb-2">
                          {t('fields.contactName')} <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id={`contacts.${index}.name`}
                          {...register(`contacts.${index}.name` as const, {
                            required: index === 0 ? t('validation.required') : false,
                          })}
                          placeholder={t('placeholders.contactName')}
                          className={errors.contacts?.[index]?.name ? 'border-destructive' : ''}
                        />
                        {errors.contacts?.[index]?.name && (
                          <p className="text-sm text-destructive">{errors.contacts[index]?.name?.message}</p>
                        )}
                      </div>

                      {/* Email del Contacto */}
                      <div className="flex flex-col gap-1">
                        <Label htmlFor={`contacts.${index}.email`} className="pb-2">
                          {t('fields.contactEmail')}
                        </Label>
                        <Input
                          id={`contacts.${index}.email`}
                          type="email"
                          {...register(`contacts.${index}.email` as const, {
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: t('validation.invalidEmail'),
                            },
                          })}
                          placeholder={t('placeholders.contactEmail')}
                          className={errors.contacts?.[index]?.email ? 'border-destructive' : ''}
                        />
                        {errors.contacts?.[index]?.email && (
                          <p className="text-sm text-destructive">{errors.contacts[index]?.email?.message}</p>
                        )}
                      </div>

                      {/* Teléfono del Contacto */}
                      <div className="flex flex-col gap-1">
                        <Label htmlFor={`contacts.${index}.phone`} className="pb-2">
                          {t('fields.contactPhone')}
                        </Label>
                        <Input
                          id={`contacts.${index}.phone`}
                          type="tel"
                          {...register(`contacts.${index}.phone` as const)}
                          placeholder={t('placeholders.contactPhone')}
                          className={errors.contacts?.[index]?.phone ? 'border-destructive' : ''}
                        />
                        {errors.contacts?.[index]?.phone && (
                          <p className="text-sm text-destructive">{errors.contacts[index]?.phone?.message}</p>
                        )}
                      </div>

                      {/* Rol del Contacto */}
                      <div className="flex flex-col gap-1">
                        <Label htmlFor={`contacts.${index}.role`} className="pb-2">
                          {t('fields.contactRole')}
                        </Label>
                        <Input
                          id={`contacts.${index}.role`}
                          {...register(`contacts.${index}.role` as const)}
                          placeholder={t('placeholders.contactRole')}
                          className={errors.contacts?.[index]?.role ? 'border-destructive' : ''}
                        />
                        {errors.contacts?.[index]?.role && (
                          <p className="text-sm text-destructive">{errors.contacts[index]?.role?.message}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Botón para agregar contacto */}
                <Button
                  type="button"
                  variant="primary"
                  size="default"
                  onClick={() => append({ name: '', email: '', phone: '', role: '' })}
                  className="w-full md:w-auto"
                >
                  <FontAwesomeIcon icon={faPlus} className="w-4 h-4 mr-2" />
                  {t('buttons.addContact')}
                </Button>
              </div>
            </div>
          </div>
          <SignedContractsModal
            ref={signedContractsModalRef}
            onSelect={(contract) => handleContractSelected(contract.envelope_id)}
          />
        </form>
      </div>
    </>
  );
};
